

















































































































































































































































































import Vue from "vue";
import ApiSearchPage from "@/components/apiSearch/components/ApiSearchPage.vue";
import CheckBox from "@/components/CheckBox.vue";
import ConfirmationBox from "@/components/ConfirmationBox.vue";
import MoneyDisplay from "@/components/MoneyDisplay.vue";
import PreAccInvoiceItems from "./PreAccInvoiceItems.vue";
import PreAccFilterPanel from "../components/PreAccFilterPanel.vue";
import PreAccountingApi from "../services/PreAccountingApi";
import preAccStore from "../services/PreAccountingStore";
import eventBus from "@/services/eventBus";
import auth from "@/services/auth";
import {
  PreAccError,
  PreAccLookups,
  PreAccStatusLog,
  PreAccStatusRef,
  PreAccUpdatedStatus,
} from "../models/PreAccounting";
import { t } from "@/plugins/i18n";

type LogTab = "errors" | "status";

interface Data {
  lookups: PreAccLookups | null;
  rowErrors: Record<string, PreAccError[]>;
  rowStatusLogs: Record<string, PreAccStatusLog[]>;
  checkedRefs: PreAccStatusRef[];
  statusActionRunning: boolean;
  reAccountRunning: boolean;
  reAccountConfirmOpen: boolean;
  takeBackConfirmVisible: boolean;
  takeBackConfirmMessage: string;
}

interface Methods {
  openPartner(item: any): void;
  toggleChecked(item: any): void;
  createNew(): void;
  openSelected(): void;
  openEdit(item: any): void;
  toggleItems(item: any): void;
  toggleLog(item: any): Promise<void>;
  changeLogTab(item: any, tab: LogTab): Promise<void>;
  loadLog(item: any, tab: LogTab): Promise<void>;
  errorsFor(item: any): PreAccError[];
  statusLogsFor(item: any): PreAccStatusLog[];
  selectedBookTypes(): number[];
  syncCheckedItems(items: any[]): void;
  checkPermission(name: string): boolean;
  applyUpdatedStatuses(updated: PreAccUpdatedStatus[]): void;
  showStatusResult(lines: { text: string; header: boolean }[]): void;
  runSetStatuses(status: number): Promise<void>;
  bookSelected(): Promise<void>;
  takeBackSelected(confirmed: boolean): Promise<void>;
  invalidateRowCaches(refs: PreAccStatusRef[]): void;
  reAccountSelected(): Promise<void>;
  onFiltersReady(): void;
}

export default Vue.extend<Data, Methods, {}, {}>({
  components: {
    ConfirmationBox,
    ApiSearchPage,
    CheckBox,
    MoneyDisplay,
    PreAccInvoiceItems,
    PreAccFilterPanel,
  },

  data() {
    return {
      lookups: null,
      rowErrors: {},
      rowStatusLogs: {},
      checkedRefs: [],
      statusActionRunning: false,
      reAccountRunning: false,
      reAccountConfirmOpen: false,
      takeBackConfirmVisible: false,
      takeBackConfirmMessage: "",
    };
  },

  async beforeMount() {
    this.lookups = await PreAccountingApi.getLookups();
  },

  mounted() {
    // :autoLoad="false" mellett az ApiSearchPage nem figyeli a kereső szövegét (csak az
    // Enter-t), ezért a gépelés közbeni keresést innen kötjük be. A reload esemény az
    // ApiSearchPage :delay-es (debounce-olt) lekérdezésére fut, így a gyors gépelés nem
    // indít kérés-özönt; a mentett szűrők visszatöltése pedig a mounted előtt megtörtént.
    this.$watch(
      () => (this.$refs.asref as any)?.apiSearch?.searchText,
      () => eventBus.$emit("pre-acc-invoices:reload")
    );
  },

  methods: {
    // Az első lekérdezést (autoLoad=false) normál esetben a szűrőpanel alapérték-írása indítja
    // a szűrő-watcheren át. Visszatöltött szűrőknél (persistFilters, navigáció utáni
    // visszatérés) alapérték-írás nincs, ezért a panel jelez, és innen indítjuk — a $nextTick
    // azért kell, mert az ApiSearchPage a szűrő-watcher elnyelő flagjeit a következő tick-ben
    // ejti el (a folyószámla nézet mintája).
    onFiltersReady() {
      this.$nextTick(() => eventBus.$emit("pre-acc-invoices:reload"));
    },

    // Kontírtétel-nézet nyitása/zárása (≡ gomb) — napló-nézetből átvált
    toggleItems(item: any) {
      if (item.selected && !item.logView) {
        item.selected = false;
        return;
      }
      // A logView utólag felvett mező — Vue.set kell a reaktivitáshoz
      Vue.set(item, "logView", false);
      item.selected = true;
    },

    // Napló-nézet nyitása/zárása (napló gomb) — csak akkor aktív, ha van hiba vagy
    // státusznapló. Alapból a hibanapló fül nyílik; ha az üres, a státusznapló.
    async toggleLog(item: any) {
      if (item.selected && item.logView) {
        item.selected = false;
        return;
      }
      const tab: LogTab = item.hasError ? "errors" : "status";
      try {
        await this.loadLog(item, tab);
        Vue.set(item, "logTab", tab);
        Vue.set(item, "logView", true);
        item.selected = true;
      } catch {
        // A backend üzenetét a globális hibakezelő már megjelenítette; hiba esetén
        // a sor a hívás előtti nyitva/zárva állapotában marad
      }
    },

    // Fülváltás a napló-nézetben: a másik napló adatait csak ekkor kérjük le
    async changeLogTab(item: any, tab: LogTab) {
      if (!tab || tab === item.logTab) {
        return;
      }
      try {
        await this.loadLog(item, tab);
        Vue.set(item, "logTab", tab);
      } catch {
        // marad az eddigi fül
      }
    },

    // A napló adatainak lekérése kulcsonkénti cache-be (csak ha még nincs)
    async loadLog(item: any, tab: LogTab) {
      const key = `${item.code}/${item.id}`;
      if (tab === "errors") {
        if (!this.rowErrors[key]) {
          const errors = await PreAccountingApi.getErrors(item.code, item.id);
          Vue.set(this.rowErrors, key, errors);
        }
      } else if (!this.rowStatusLogs[key]) {
        const logs = await PreAccountingApi.getStatusLog(item.code, item.id);
        Vue.set(this.rowStatusLogs, key, logs);
      }
    },

    errorsFor(item: any): PreAccError[] {
      return this.rowErrors[`${item.code}/${item.id}`] ?? [];
    },

    statusLogsFor(item: any): PreAccStatusLog[] {
      return this.rowStatusLogs[`${item.code}/${item.id}`] ?? [];
    },

    openPartner(item: any) {
      this.$router.push(`/partners/${item.partner}`);
    },

    // A pipa-lista lapozáson át is megmarad — ebből nyílik a többszámlás szerkesztő
    // és a csoportos státusz állítás. Az accStatus a K2D optimista egyeztetéséhez kell
    // (a gridben LÁTOTT kontír státusznak kell egyeznie a DB-belivel).
    toggleChecked(item: any) {
      item.selectedStatus = !item.selectedStatus;
      const idx = this.checkedRefs.findIndex((x) => x.code === item.code && x.invNo === item.id);
      if (item.selectedStatus && idx < 0) {
        this.checkedRefs.push({ code: item.code, invNo: item.id, accStatus: item.accStatus });
      } else if (!item.selectedStatus && idx >= 0) {
        this.checkedRefs.splice(idx, 1);
      }
    },

    // Lapozás/újratöltés után a pipák visszaállítása és a tárolt kontír státusz
    // frissítése a grid friss értékéből (K2D-ben a grid sorai meg sem szűnnek) —
    // az ApiSearchPage @items eseményéből
    syncCheckedItems(items: any[]): void {
      (items ?? []).forEach((item) => {
        const ref = this.checkedRefs.find((x) => x.code === item.code && x.invNo === item.id);
        if (ref) {
          if (!item.selectedStatus) item.selectedStatus = true;
          ref.accStatus = item.accStatus;
        }
      });
    },

    checkPermission(name: string): boolean {
      const menuPerm = auth.menuPermissions.find((x) => x.name == name);
      return menuPerm?.isGranted ?? false;
    },

    // A sikeresen átállított sorok friss státusza a pipa-listába (K2D dw_master.setItem)
    applyUpdatedStatuses(updated: PreAccUpdatedStatus[]) {
      (updated ?? []).forEach((u) => {
        const ref = this.checkedRefs.find((x) => x.code === u.code && x.invNo === u.invNo);
        if (ref) ref.accStatus = u.accStatus;
      });
    },

    // Összegző üzenet a státusz állítás / auto-kontír után (K2D MessageBox:
    // KSZSNSA / AKSKSOHVRLAB) — a megszokott jobb felső figyelmeztetésként,
    // a fejléc sorok félkövéren, a tételek alatta külön sorban
    showStatusResult(lines: { text: string; header: boolean }[]) {
      const html = lines
        .map((l) => (l.header ? `<b>${l.text}</b>` : `&nbsp;&nbsp;${l.text}`))
        .join("\n");
      eventBus.$emit("warn", html);
    },

    // K2D ue_menu_acc_status_csoportos Case 2/3/5/9: csoportos státusz állítás.
    // Nincs előzetes megerősítő kérdés (a K2D-ben ki van kommentelve); a hibák
    // számlánként gyűlnek és a végén EGY összegző üzenet jön.
    async runSetStatuses(status: number) {
      if (this.checkedRefs.length === 0) return;
      this.statusActionRunning = true;
      try {
        const result = await PreAccountingApi.setStatuses(status, this.checkedRefs);
        this.applyUpdatedStatuses(result.updated);

        const lines: { text: string; header: boolean }[] = [];
        if (result.closedVatWarnings.length > 0) {
          lines.push({
            text: t("PreAccounting_AKovetkezoSzamlakKonyvelesiDatumaOlyan"),
            header: true,
          });
          result.closedVatWarnings.forEach((w) => lines.push({ text: w, header: false }));
        }
        if (result.failures.length > 0) {
          lines.push({
            text: t("PreAccounting_AKovetkezoSzamlakStatuszanNemSikerult"),
            header: true,
          });
          result.failures.forEach((f) => lines.push({ text: f, header: false }));
        }
        if (lines.length > 0) this.showStatusResult(lines);
        else eventBus.$emit("success", t("PreAccounting_SikeresStatuszAllitas"));
      } catch {
        // A backend üzenetét a globális hibakezelő már megjelenítette
      } finally {
        this.statusActionRunning = false;
        // Hibaágon is újratöltünk — részleges átállítás is történhetett
        this.invalidateRowCaches(this.checkedRefs);
        eventBus.$emit("pre-acc-invoices:reload");
      }
    },

    // K2D Case 4 "Feladva": a kijelölt (Feladható) számlák feladása a belső főkönyvbe
    // (spnaccinvsbooking). Az "egyből feladva" jogosultsággal a nem 3-as státuszúakra
    // előbb automatikusan lefut a Feladható-ág.
    async bookSelected() {
      if (this.checkedRefs.length === 0) return;
      this.statusActionRunning = true;
      try {
        // TODO: PREACCDIRECTPOST jogosultsági kulcs felvétele után a jog ténylegesen
        // kiosztható — addig a K2D alapesete él (nem 3-as kijelöltnél hibaüzenet)
        const allowAutoPromote = this.checkPermission("PREACCDIRECTPOST");
        const result = await PreAccountingApi.bookInvoices(this.checkedRefs, allowAutoPromote);
        this.applyUpdatedStatuses(result.updated);

        const lines: { text: string; header: boolean }[] = [];
        if (result.closedVatWarnings.length > 0) {
          lines.push({
            text: t("PreAccounting_AKovetkezoSzamlakKonyvelesiDatumaOlyan"),
            header: true,
          });
          result.closedVatWarnings.forEach((w) => lines.push({ text: w, header: false }));
        }
        if (result.failures.length > 0) {
          lines.push({
            text: t("PreAccounting_AKovetkezoSzamlakStatuszanNemSikerult"),
            header: true,
          });
          result.failures.forEach((f) => lines.push({ text: f, header: false }));
        }
        if (result.vatReturnWarning) {
          lines.push({ text: result.vatReturnWarning, header: true });
        }
        if (lines.length > 0) this.showStatusResult(lines);
        else eventBus.$emit("success", t("PreAccounting_AKijeloltSzamlakFeladvaAFokonyvbe"));
      } catch {
        // A backend üzenetét a globális hibakezelő már megjelenítette
      } finally {
        this.statusActionRunning = false;
        // Hibaágon is újratöltünk — az auto-Feladható előfutás részlegesen commitálhatott
        this.invalidateRowCaches(this.checkedRefs);
        eventBus.$emit("pre-acc-invoices:reload");
      }
    },

    // K2D Case 31 "Feladvából feladhatóba": visszavétel a főkönyvből (spjbookhanding_del).
    // Ha a visszaveendő számla nyitott ÁFA bevallásban szerepel, Igen/Nem kérdés jön
    // (K2D f_control_vatret) — Igen esetén confirmed=true-val fut újra a hívás.
    async takeBackSelected(confirmed: boolean) {
      if (this.checkedRefs.length === 0) return;
      this.takeBackConfirmVisible = false;
      this.statusActionRunning = true;
      let reload = true;
      try {
        const result = await PreAccountingApi.takeBackInvoices(this.checkedRefs, confirmed);

        if (result.needsConfirmation) {
          // Kérdés jött vissza — a szerveren semmi sem módosult, felesleges újratölteni
          reload = false;
          this.takeBackConfirmMessage = result.confirmMessage;
          this.takeBackConfirmVisible = true;
          return;
        }

        this.applyUpdatedStatuses(result.updated);
        if (result.failures.length > 0) {
          const lines: { text: string; header: boolean }[] = [
            { text: t("PreAccounting_AKovetkezoSzamlakStatuszanNemSikerult"), header: true },
          ];
          result.failures.forEach((f) => lines.push({ text: f, header: false }));
          this.showStatusResult(lines);
        } else {
          eventBus.$emit("success", t("PreAccounting_AKijeloltSzamlakVisszaveveAFokonyvbol"));
        }
      } catch {
        // A backend üzenetét a globális hibakezelő már megjelenítette
      } finally {
        this.statusActionRunning = false;
        if (reload) {
          this.invalidateRowCaches(this.checkedRefs);
          eventBus.$emit("pre-acc-invoices:reload");
        }
      }
    },

    // A lenyitó-nézetek kulcsonkénti cache-e (hibalista, státusznapló) a csoportos
    // műveletek után elavul — töröljük, hogy a következő nyitás frissen kérje le
    invalidateRowCaches(refs: PreAccStatusRef[]) {
      refs.forEach((ref) => {
        const key = `${ref.code}/${ref.invNo}`;
        Vue.delete(this.rowErrors, key);
        Vue.delete(this.rowStatusLogs, key);
      });
    },

    // K2D pb_cont (sheet): csoportos automatikus (újra)kontírozás a kijelölt sorokon,
    // számlánként külön hívással. MIX kihagyva (a K2D sheet gombja is átlépi, azt csak
    // a saját ablaka kontírozza), csak a 2/5/7/8/9 kontír státusz mehet (backend:
    // ReAccountPreAccInvoice.ReAccountableAccStatuses); hibánál a ciklus megáll
    // (K2D Exit), a már újrakontírozott számlák módosítása megmarad.
    async reAccountSelected() {
      this.reAccountConfirmOpen = false;
      if (this.checkedRefs.length === 0) return;
      this.reAccountRunning = true;
      // Snapshot: a pipa-lista a hívások alatt is változhat (a sor-checkbox nem tiltott);
      // az elemek ugyanazok a ref objektumok, így az accStatus-frissítés a valódira megy
      const refs = [...this.checkedRefs];
      let done = 0;
      const erroredLines: string[] = [];
      let stopped: PreAccStatusRef | null = null;
      const processed: PreAccStatusRef[] = [];
      try {
        for (const ref of refs) {
          if (ref.code === "MIX" || ![2, 5, 7, 8, 9].includes(ref.accStatus)) continue;
          try {
            // Az originalAccStatus a gridben LÁTOTT státusz — a K2D optimista
            // egyeztetése: ha a DB-ben már más van, a backend hibával megáll
            const result = await PreAccountingApi.reAccount(ref.code, ref.invNo, ref.accStatus);
            ref.accStatus = result.accStatus;
            processed.push(ref);
            done++;
            // 7/8-as új státusz = a kontír-motor hibalistával zárt (K2D-ben ezt
            // csak a sor színe jelzi — itt az összegzőbe is bekerül)
            if (result.accStatus === 7 || result.accStatus === 8) {
              erroredLines.push(`${ref.code}/${ref.invNo} — ${result.accStatusName}`);
            }
          } catch {
            // A backend üzenetét a globális hibakezelő már megjelenítette; K2D: Exit
            stopped = ref;
            processed.push(ref);
            break;
          }
        }

        const lines: { text: string; header: boolean }[] = [];
        if (erroredLines.length > 0) {
          lines.push({ text: t("PreAccounting_AKovetkezoSzamlakKontirozasaHibalistavalZarult"), header: true });
          erroredLines.forEach((e) => lines.push({ text: e, header: false }));
        }
        if (stopped) {
          lines.push({
            text:
              t("PreAccounting_AzUjrakontirozasMegalltAZSzamlanal", [stopped.code, stopped.invNo]) +
              t("PreAccounting_EddigSzamlaLettUjrakontirozvaATovabbi", [done]),
            header: true,
          });
        }
        if (lines.length > 0) {
          this.showStatusResult(lines);
        } else if (done === 0) {
          eventBus.$emit("warn", t("PreAccounting_AKijeloltekKozottNincsUjrakontirozhatoSzamla"));
        } else {
          const skipped = refs.length - done;
          eventBus.$emit(
            "success",
            skipped > 0
              ? t("PreAccounting_SzamlaUjrakontirozvaKihagyvaMIXIlletveNem", [done, skipped])
              : t("PreAccounting_AKijeloltSzamlakUjrakontirozva")
          );
        }
      } catch {
        // Váratlan hiba — a lista frissítése a finally-ben így is lefut
      } finally {
        this.reAccountRunning = false;
        // K2D: a végén a lista frissül (dw_tartozik.of_retrieve) — megállás után is,
        // mert az addig kontírozott számlák már módosultak
        if (processed.length > 0) {
          this.invalidateRowCaches(processed);
          eventBus.$emit("pre-acc-invoices:reload");
        }
      }
    },

    // Új kontírozás = új MIX (vegyes) bizonylat felvitele a semmiből
    // (K2D w_response_mix pb_insert) — nem igényel kijelölést
    createNew() {
      this.$router.push("/pre-accounting/create");
    },

    // K2D ue_response_open(true): a kijelölt számlák egy szerkesztőben, lapozóval;
    // MIX és nem-MIX keverése tiltott (K2D-szabály), tiszta MIX-lista mehet
    openSelected() {
      if (this.checkedRefs.length === 0) {
        eventBus.$emit("warn", t("PreAccounting_NemJeloltKiEgyetlenSzamlatSem"));
        return;
      }
      const mixCount = this.checkedRefs.filter((x) => x.code === "MIX").length;
      if (mixCount > 0 && mixCount < this.checkedRefs.length) {
        eventBus.$emit("warn", t("PreAccounting_MIXEsNemMIXBizonylatNem"));
        return;
      }
      preAccStore.invoiceRefs = [...this.checkedRefs];
      preAccStore.currentIndex = 0;
      this.$router.push("/pre-accounting/edit");
    },

    openEdit(item: any) {
      this.$router.push(`/pre-accounting/${item.code}/${item.id}/edit`);
    },

    // A "Könyvelési típus" pipák a tétel-listát szűrik (K2D: dw_tartozik szűrője).
    // Ha minden típus pipa, nem adunk át semmit — így nincs szűrés. A szűrőmodell az
    // ApiSearchPage-é (ref) — a details slot nem kapja meg; a pipák változása újratölti
    // a listát, így a lenyíló tétel-nézet friss értékkel nyílik.
    selectedBookTypes(): number[] {
      const apiSearch = (this.$refs.asref as any)?.apiSearch;
      const selected: string[] = apiSearch?.filter?.filters?.selectedBookTypes?.selectedOptions ?? [];
      const all = this.lookups?.bookTypes?.length ?? 0;
      if (selected.length === 0 || (all > 0 && selected.length >= all)) return [];
      return selected.map((x) => Number(x)).filter((x) => x > 0);
    },
  },

  computed: {
    // K2D pfc_open: Libra/IMX-es telepítésen a hat státusz-menüpont helyett a külső
    // rendszeres feladás (m_libra / m_imx) látszana — az nincs portolva, a menü rejtett
    statusMenuVisible(): boolean {
      if (!this.lookups) return false;
      return !this.lookups.isLibra && !this.lookups.isImx;
    },

    // A lenyitott hibalista-nézet oszlopai — width nélkül, a table-layout: fixed
    // osztja el őket egyenlően a teljes szélességen
    errorColumns: () => [
      { field: "errTypeName", title: t("HibaTipusa") },
      { field: "note", title: t("Megjegyzes") },
    ],

    // A lenyitott státusznapló-nézet oszlopai (K2D d_g_llaccaccstatus)
    statusLogColumns: () => [
      { field: "oldValue", title: t("RegiErtek") },
      { field: "newValue", title: t("UjErtek") },
      { field: "userName", title: t("Felhasznalo") },
      { field: "updDate", title: t("Datum") },
      { field: "updTime", title: t("Ido") },
      { field: "appl", title: t("Alkalmazas") },
    ],

    // Az ApiSearchPage ebből építi a szerver felé menő filter/sort modellt és a Kendo grid oszlopait
    columns: () => [
      { cell: "commandTemplate", filterable: false, sortable: false, width: 225 },
      { field: "prefix", title: t("Prefix"), width: 85 },
      { field: "typeCode", title: t("Tipuskod"), width: 95 },
      { field: "id", title: t("Szamlaszam"), width: 120, fieldClass: "right_text" },
      { field: "outerInvNo", title: t("KulsoSzamlaszam"), width: 140, fieldClass: "right_text" },
      { field: "partnerName", title: t("Partner"), cell: "partnerTemplate", width: 400 },
      { field: "invDate", title: t("SzamlaDatuma"), width: 140, fieldClass: "right_text" },
      { field: "netto", title: t("Netto"), cell: "nettoTemplate", filterable: false, sortable: false, width: 130 },
      { field: "vat", title: t("AFA"), cell: "vatTemplate", filterable: false, sortable: false, width: 120 },
      { field: "total", title: t("Brutto"), cell: "totalTemplate", filterable: false, sortable: false, width: 130 },
      { field: "currency", title: t("Penznem"), width: 60 },
      { field: "payModeName", title: t("FizetesiMod"), width: 120 },
      { field: "shipDate", title: t("PreAccounting_TeljDatAfa_2"), width: 100, fieldClass: "right_text" },
      { field: "accStatusName", title: t("KonyvelesAllapot"), cell: "accStatusTemplate", width: 170 },
      { field: "statusName", title: t("Allapot"), width: 120 },
      { field: "remark", title: t("Megjegyzes"), cell: "remarkTemplate", width: 210 },
    ],
  },
});
